import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Divider,
  FormControlLabel,
  Grid2 as Grid,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Facebook01Icon,
  InstagramIcon,
  Mail01Icon,
  NewTwitterRectangleIcon,
  Restaurant01Icon,
  Store01Icon,
  TiktokIcon,
  WhatsappIcon,
  PlusSignIcon,
} from "@hugeicons/core-free-icons";
import { useFormik } from "formik";
import * as Yup from "yup";
import PageHeader from "@/components/PageHeader";
import Alias from "@/components/Alias";
import ImageUpload from "@/components/ImageUpload";
import ColorPicker from "@/components/ColorPicker";
import useSnackbar from "@/hooks/useSnackbar";
import { useAppDispatch, useAppSelector } from "@/hooks";
import {
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
} from "@/features/organizations/api/organizationsApi";
import { useCreateAccessMutation, useSendInviteMutation } from "@/api/accessApi";
import { useAddMenuMutation } from "@/features/organizations/api/menusApi";
import {
  useCreateOrganizationAliasMutation,
  useCreateWebsiteMutation,
} from "@/features/organizations/api/websiteApi";
import { uploadOrganizationImage } from "@/features/organizations/slice";
import { DEFAULT_MENU_TEMPLATE, DEFAULT_WEBSITE_TEMPLATE } from "@/constants/configs";
import { PlanType } from "@/types";
import { sanitizePhone } from "@/utils/phones";
import LoadingButton from "@mui/lab/LoadingButton";

interface NewClientFormValues {
  title: string;
  industry: "restaurant" | "other";
  slogan: string;
  alias: string;
  logo: File | null;
  isotype: File | null;
  primaryColor: string;
  secondaryColor: string;
  inviteEnabled: boolean;
  adminEmail: string;
  whatsapp: string;
  instagram: string;
  facebook: string;
  tiktok: string;
  x: string;
}

const validationSchema = Yup.object().shape({
  title: Yup.string()
    .trim()
    .required("El nombre del comercio es requerido"),
  industry: Yup.string()
    .oneOf(["restaurant", "other"])
    .required("Selecciona un tipo de comercio"),
  slogan: Yup.string().optional(),
  alias: Yup.string()
    .trim()
    .required("El alias de enlace público es requerido")
    .matches(
      /^[a-z0-9-]+$/,
      "El alias solo puede contener letras minúsculas, números y guiones"
    ),
  primaryColor: Yup.string().required("Color primario requerido"),
  secondaryColor: Yup.string().required("Color secundario requerido"),
  inviteEnabled: Yup.boolean(),
  adminEmail: Yup.string().when("inviteEnabled", {
    is: true,
    then: (schema) =>
      schema
        .trim()
        .email("Ingresa un correo electrónico válido")
        .required("El correo del administrador es requerido"),
    otherwise: (schema) => schema.optional(),
  }),
  whatsapp: Yup.string().optional(),
  instagram: Yup.string().optional(),
  facebook: Yup.string().optional(),
  tiktok: Yup.string().optional(),
  x: Yup.string().optional(),
});

const initialValues: NewClientFormValues = {
  title: "",
  industry: "restaurant",
  slogan: "",
  alias: "",
  logo: null,
  isotype: null,
  primaryColor: "#06175D",
  secondaryColor: "#D0F857",
  inviteEnabled: true,
  adminEmail: "",
  whatsapp: "",
  instagram: "",
  facebook: "",
  tiktok: "",
  x: "",
};

const NewClientPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [openSnackbar] = useSnackbar();
  const user = useAppSelector((state) => state.auth.user);
  const [isAliasValid, setIsAliasValid] = useState(false);

  // Mutations
  const [createOrganization] = useCreateOrganizationMutation();
  const [updateOrganization] = useUpdateOrganizationMutation();
  const [createAccess] = useCreateAccessMutation();
  const [createAliasMutation] = useCreateOrganizationAliasMutation();
  const [addMenu] = useAddMenuMutation();
  const [createWebsite] = useCreateWebsiteMutation();
  const [sendInvite] = useSendInviteMutation();

  const formik = useFormik<NewClientFormValues>({
    initialValues,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      if (!isAliasValid) {
        openSnackbar("El alias seleccionado no está disponible o es inválido", "warning");
        setSubmitting(false);
        return;
      }

      if (!user?.uid) {
        openSnackbar("Debes haber iniciado sesión para crear un cliente", "error");
        setSubmitting(false);
        return;
      }

      try {
        // 1. Create Organization
        const orgPayload: any = {
          title: values.title.trim(),
          industry: values.industry === "other" ? "other" : "food-and-beverages",
          alias: values.alias.trim(),
          slogan: values.slogan.trim() || undefined,
          primaryColor: values.primaryColor || "#06175D",
          secondaryColor: values.secondaryColor || "#D0F857",
          plan: PlanType.FREE,
          createdBy: user.uid,
          createdAt: Date.now(),
        };

        if (values.whatsapp.trim()) {
          orgPayload.whatsapp = Number(sanitizePhone(values.whatsapp.trim()));
        }
        if (values.instagram.trim()) orgPayload.instagram = values.instagram.trim();
        if (values.facebook.trim()) orgPayload.facebook = values.facebook.trim();
        if (values.tiktok.trim()) orgPayload.tiktok = values.tiktok.trim();
        if (values.x.trim()) orgPayload.x = values.x.trim();

        const orgResult: any = await createOrganization(orgPayload);

        if (orgResult?.error || !orgResult?.data?.id) {
          throw new Error(orgResult?.error?.statusText || "Error al crear la organización");
        }

        const orgId = orgResult.data.id;

        // 2. Upload Logo & Isotype if provided
        const logosPayload: { logo?: string; isotype?: string } = {};

        if (values.logo) {
          try {
            const logoRes: any = await dispatch(
              uploadOrganizationImage({ organizationId: orgId, file: values.logo })
            );
            if (logoRes?.payload?.image) {
              logosPayload.logo = logoRes.payload.image;
            }
          } catch (imgErr) {
            console.error("Error al subir logotipo:", imgErr);
          }
        }

        if (values.isotype) {
          try {
            const isotypeRes: any = await dispatch(
              uploadOrganizationImage({ organizationId: orgId, file: values.isotype })
            );
            if (isotypeRes?.payload?.image) {
              logosPayload.isotype = isotypeRes.payload.image;
            }
          } catch (imgErr) {
            console.error("Error al subir isotipo:", imgErr);
          }
        }

        if (logosPayload.logo || logosPayload.isotype) {
          await updateOrganization({
            organizationId: orgId,
            payload: logosPayload,
          });
        }

        // 3. Register Alias
        try {
          await createAliasMutation({
            alias: values.alias.trim(),
            organizationId: orgId,
          });
        } catch (aliasErr) {
          console.error("Error al registrar alias:", aliasErr);
        }

        // 4. Create Initial Menu
        try {
          const isOther = values.industry === "other";
          await addMenu({
            draft: {
              title: isOther ? "Catálogo Principal" : "Menú Principal",
              description: isOther
                ? "Catálogo de ejemplo, edítalo o elimínalo lo antes posible"
                : "Menú de ejemplo, edítalo o elimínalo lo antes posible",
              isPublished: true,
              template: DEFAULT_MENU_TEMPLATE,
            },
            organizationId: orgId,
          });
        } catch (menuErr) {
          console.error("Error al crear menú inicial:", menuErr);
        }

        // 5. Create Initial Website
        try {
          const websiteRes: any = await createWebsite({
            organizationId: orgId,
            website: {
              title: "Sitio principal",
              template: DEFAULT_WEBSITE_TEMPLATE,
              isPublished: true,
            },
          });

          if (websiteRes?.data) {
            await updateOrganization({
              organizationId: orgId,
              payload: {
                website: websiteRes.data,
              },
            });
          }
        } catch (webErr) {
          console.error("Error al crear sitio web inicial:", webErr);
        }

        // 6. Assign Creator Access
        try {
          await createAccess({
            organizationId: orgId,
            organizationTitle: values.title.trim(),
          });
        } catch (accErr) {
          console.error("Error al asignar acceso:", accErr);
        }

        // 7. Send Owner Admin Invite if enabled
        if (values.inviteEnabled && values.adminEmail.trim()) {
          try {
            await sendInvite({
              email: values.adminEmail.trim().toLowerCase(),
              role: "admin",
              orgId: orgId,
            }).unwrap();
            openSnackbar("Organización creada e invitación enviada con éxito", "success");
          } catch (invErr: any) {
            console.error("Error enviando invitación:", invErr);
            openSnackbar(
              "Organización creada, pero hubo un error enviando el correo de invitación",
              "warning"
            );
          }
        } else {
          openSnackbar("Cliente creado exitosamente", "success");
        }

        // 8. Redirect to client details page
        navigate(`/super-admin/clients/${orgId}`);
      } catch (error: any) {
        console.error("Error en la creación del cliente:", error);
        openSnackbar(error?.message || "Ocurrió un error al crear el cliente", "error");
        setSubmitting(false);
      }
    },
  });

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    formik.setFieldValue("title", val);

    const prevSlug = formik.values.title
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");

    if (!formik.values.alias || formik.values.alias === prevSlug) {
      const nextSlug = val
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "");
      formik.setFieldValue("alias", nextSlug);
    }
  };

  return (
    <>
      <PageHeader
        title="Nuevo Cliente"
        action={
          <LoadingButton
            variant="contained"
            color="success"
            size="medium"
            loading={formik.isSubmitting}
            onClick={() => formik.handleSubmit()}
            startIcon={<HugeiconsIcon icon={PlusSignIcon} size={18} />}
          >
            Crear Cliente
          </LoadingButton>
        }
      />
      <Stack p={{ xs: 1.5, sm: 2, md: 3 }} spacing={2.5} sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>

        <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
          {/* Left Column: Organization & Brand Info */}
          <Grid size={{ xs: 12, md: 7 }}>
            <Stack spacing={{ xs: 2, sm: 2.5, md: 3 }}>
              {/* General Info Card */}
              <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h3" mb={2}>
                  Información del cliente
                </Typography>

                <Stack spacing={2.5}>
                  <TextField
                    fullWidth
                    name="title"
                    label="Nombre del Comercio"
                    placeholder="Ej: La Pizzería Central"
                    value={formik.values.title}
                    onChange={handleTitleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.title && Boolean(formik.errors.title)}
                    helperText={formik.touched.title && formik.errors.title}
                    required
                  />

                  <Stack spacing={1}>
                    <Typography variant="h3">
                      Tipo de comercio
                    </Typography>
                    <ToggleButtonGroup
                      value={formik.values.industry}
                      exclusive
                      onChange={(_, val) => val && formik.setFieldValue("industry", val)}
                      fullWidth
                      size="small"
                      sx={{
                        "& .MuiToggleButton-root": {
                          py: 1,
                          gap: 1,
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        },
                      }}
                    >
                      <ToggleButton value="restaurant">
                        <HugeiconsIcon icon={Restaurant01Icon} size={18} />
                        Restaurante
                      </ToggleButton>
                      <ToggleButton value="other">
                        <HugeiconsIcon icon={Store01Icon} size={18} />
                        Otro (Tienda o Comercio)
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Stack>

                  <TextField
                    fullWidth
                    name="slogan"
                    label="Slogan o Descripción Corta"
                    placeholder="Ej: Las mejores pizzas artesanales de la ciudad"
                    value={formik.values.slogan}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.slogan && Boolean(formik.errors.slogan)}
                    helperText={formik.touched.slogan && formik.errors.slogan}
                  />

                  <Stack spacing={1}>
                    <Typography variant="h3">
                      Alias de enlace público (URL)
                    </Typography>
                    <Alias
                      value={formik.values.alias}
                      onChange={(val) => {
                        formik.setFieldValue("alias", val);
                        formik.setFieldTouched("alias", true, false);
                      }}
                      onSuccess={() => setIsAliasValid(true)}
                      onFail={() => setIsAliasValid(false)}
                    />
                    {formik.touched.alias && formik.errors.alias && (
                      <Typography variant="caption" color="error">
                        {formik.errors.alias}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      Tus clientes accederán a tu menú digital a través de esta dirección web.
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>

              {/* Visual Identity Card */}
              <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h3" mb={2}>
                  Identidad visual
                </Typography>

                <Stack spacing={2.5}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={{ xs: 2, sm: 3 }}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <ImageUpload
                      label="Logotipo"
                      value={formik.values.logo}
                      onChange={(file) => formik.setFieldValue("logo", file)}
                    />
                    <ImageUpload
                      label="Isotipo"
                      value={formik.values.isotype}
                      onChange={(file) => formik.setFieldValue("isotype", file)}
                    />
                  </Stack>

                  <Divider />

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Stack spacing={1}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          Color primario
                        </Typography>
                        <ColorPicker
                          condensed
                          name="primaryColor"
                          value={formik.values.primaryColor}
                          onChange={(e) => formik.setFieldValue("primaryColor", e.target.value)}
                        />
                      </Stack>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Stack spacing={1}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          Color secundario
                        </Typography>
                        <ColorPicker
                          condensed
                          name="secondaryColor"
                          value={formik.values.secondaryColor}
                          onChange={(e) => formik.setFieldValue("secondaryColor", e.target.value)}
                        />
                      </Stack>
                    </Grid>
                  </Grid>
                </Stack>
              </Paper>
            </Stack>
          </Grid>

          {/* Right Column: Contact & Admin Invitation */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Stack spacing={{ xs: 2, sm: 2.5, md: 3 }}>
              {/* Admin Invitation Card */}
              <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={1}
                  mb={1.5}
                >
                  <Typography variant="h3">
                    Invitación al propietario
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formik.values.inviteEnabled}
                        onChange={(e) => formik.setFieldValue("inviteEnabled", e.target.checked)}
                        color="primary"
                        size="small"
                      />
                    }
                    label=""
                    sx={{ m: 0 }}
                  />
                </Stack>

                <Typography variant="body2" color="text.secondary" mb={2}>
                  Se enviará una invitación por correo para que el dueño del negocio tome el control
                  de la organización como Administrador.
                </Typography>

                {formik.values.inviteEnabled && (
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      type="email"
                      name="adminEmail"
                      label="Correo Electrónico del Administrador"
                      placeholder="propietario@ejemplo.com"
                      value={formik.values.adminEmail}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.adminEmail && Boolean(formik.errors.adminEmail)}
                      helperText={formik.touched.adminEmail && formik.errors.adminEmail}
                      required
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <HugeiconsIcon icon={Mail01Icon} size={18} />
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      El usuario recibirá un correo con el enlace para reclamar su cuenta y administrar
                      menús, productos y configuraciones.
                    </Alert>
                  </Stack>
                )}
              </Paper>

              {/* Contact and Social Networks Card */}
              <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h3" mb={2}>
                  Contacto y redes sociales
                </Typography>

                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    name="whatsapp"
                    label="WhatsApp / Teléfono"
                    placeholder="88888888"
                    value={formik.values.whatsapp}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <HugeiconsIcon icon={WhatsappIcon} size={18} />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />

                  <TextField
                    fullWidth
                    name="instagram"
                    label="Instagram"
                    placeholder="usuario_instagram"
                    value={formik.values.instagram}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <HugeiconsIcon icon={InstagramIcon} size={18} />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />

                  <TextField
                    fullWidth
                    name="facebook"
                    label="Facebook"
                    placeholder="usuario_facebook"
                    value={formik.values.facebook}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <HugeiconsIcon icon={Facebook01Icon} size={18} />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />

                  <TextField
                    fullWidth
                    name="tiktok"
                    label="TikTok"
                    placeholder="usuario_tiktok"
                    value={formik.values.tiktok}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <HugeiconsIcon icon={TiktokIcon} size={18} />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />

                  <TextField
                    fullWidth
                    name="x"
                    label="X (Twitter)"
                    placeholder="usuario_x"
                    value={formik.values.x}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <HugeiconsIcon icon={NewTwitterRectangleIcon} size={18} />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </Stack>
              </Paper>
            </Stack>
          </Grid>
        </Grid>
      </Stack>
    </>
  );
};

export default NewClientPage;
